import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Table, 
    Button, 
    Row, 
    Col, 
    Statistic,
    Tag,
    Space,
    Input,
    Select,
    Typography,
    Tooltip,
    message,
    Badge,
    Spin,
    Modal,
    Descriptions,
    Divider,
    Progress,
} from 'antd';
import { 
    DollarOutlined,
    DownloadOutlined,
    SearchOutlined,
    TrophyOutlined,
    CalendarOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined,
    ExclamationCircleOutlined,
    TeamOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { CompanyPaymentRecord, PaymentStats, PaymentFilters } from '../../../types/company.types';
import companyService from '../../../services/company/company.service';
import dayjs from 'dayjs';
import { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { Title, Text } = Typography;

const PaymentReportsPage: React.FC = () => {
    const [payments, setPayments] = useState<CompanyPaymentRecord[]>([]);
    const [stats, setStats] = useState<PaymentStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<PaymentFilters>({});
    const [searchText, setSearchText] = useState('');
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<any>(null);

    useEffect(() => {
        fetchPaymentData();
    }, [filters]);

    // KPI hesaplama: backend KPI dönebilir; yine de fallback hesaplayalım
    const calculatePaymentStats = (items: any[], kpis?: any): PaymentStats => {
        const coalesce = (v: any, def = 0) => (v == null || Number.isNaN(Number(v)) ? def : Number(v));
        if (kpis) {
            return {
                totalRevenue: coalesce(kpis.totalRevenue),
                monthlyRevenue: coalesce(kpis.thisMonthRevenue),
                totalTransactions: items.length,
                activeSubscriptions: coalesce(kpis.activeSubscriptions),
                trialUsers: coalesce(kpis.trialCompanies),
                canceledSubscriptions: coalesce(kpis.cancelledCompanies),
                averageRevenuePerUser: coalesce(kpis.revenuePerUser),
                averageOrderValue: 0,
                revenueGrowth: 0,
                topPayingCompanies: [],
                monthlyRevenueData: [],
                paymentStatusDistribution: { paid: 0, pending: 0, failed: 0, refunded: 0 },
                planTypeDistribution: { premium: 0, basic: 0, enterprise: 0 }
            };
        }
        // Basit fallback hesaplama
        const totalRevenue = items.reduce((sum, p) => sum + (p.amountPaid || 0), 0);
        const currentMonth = dayjs().format('YYYY-MM');
        const monthlyRevenue = items
          .filter(p => p.paidAt && dayjs(p.paidAt).format('YYYY-MM') === currentMonth)
          .reduce((sum, p) => sum + (p.amountPaid || 0), 0);
        const activeSubscriptions = items.filter(p => p.expiryDate && dayjs(p.expiryDate).isAfter(dayjs())).length;
        return {
            totalRevenue,
            monthlyRevenue,
            totalTransactions: items.length,
            activeSubscriptions,
            trialUsers: 0,
            canceledSubscriptions: 0,
            averageRevenuePerUser: 0,
            averageOrderValue: 0,
            revenueGrowth: 0,
            topPayingCompanies: [],
            monthlyRevenueData: [],
            paymentStatusDistribution: { paid: 0, pending: 0, failed: 0, refunded: 0 },
            planTypeDistribution: { premium: 0, basic: 0, enterprise: 0 }
        };
    };

    // Filtreleri uygula
    const applyFilters = (payments: CompanyPaymentRecord[], filters: PaymentFilters): CompanyPaymentRecord[] => {
        return payments.filter(payment => {
            if (filters.paymentStatus && payment.paymentStatus !== filters.paymentStatus) {
                return false;
            }
            if (filters.planType && payment.planType !== filters.planType) {
                return false;
            }
            if (filters.dateRange && filters.dateRange.length === 2) {
                const paymentDate = dayjs(payment.paymentDate);
                const startDate = dayjs(filters.dateRange[0]);
                const endDate = dayjs(filters.dateRange[1]);
                if (!paymentDate.isBetween(startDate, endDate, 'day', '[]')) {
                    return false;
                }
            }
            return true;
        });
    };

    const fetchPaymentData = async () => {
        try {
            setLoading(true);
            // Gerçek API'den ödeme raporları
            const response = await companyService.getPaymentReports({
                q: filters.companyName,
                status: filters.paymentStatus as any,
                planType: filters.planType as any,
            });

            const kpis = response?.kpis;
            const items = response?.items || [];

            const mapped: CompanyPaymentRecord[] = items.map((it: any) => ({
                id: String(it.id),
                companyId: it.companyId,
                companyName: it.companyName,
                planType: it.planType,
                amount: typeof it.amountPaid === 'number' ? it.amountPaid : Number(it.amountPaid || 0),
                currency: it.currency || 'TRY',
                paymentStatus: it.paymentStatus,
                paymentDate: it.paidAt || it.startDate,
                expiryDate: it.expiryDate || null,
                userLimit: it.purchasedLimit,
                description: it.description,
                // modal için ham veri
                raw: it
            }));

            const statsData = calculatePaymentStats(items, kpis);
            const filteredPayments = applyFilters(mapped, filters);

            setPayments(filteredPayments);
            setStats(statsData);
        } catch (error) {
            message.error('Ödeme verileri yüklenirken hata oluştu');
            // Hata durumunda boş state'ler bırak
            setPayments([]);
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number, currency: string = 'TRY') => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'paid': return 'success';
            case 'pending': return 'processing';
            case 'failed': return 'error';
            case 'refunded': return 'warning';
            default: return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'paid': return 'Ödendi';
            case 'pending': return 'Bekliyor';
            case 'failed': return 'Başarısız';
            case 'refunded': return 'İade Edildi';
            default: return status;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'paid': return <CheckCircleOutlined />;
            case 'pending': return <ClockCircleOutlined />;
            case 'failed': return <CloseCircleOutlined />;
            case 'refunded': return <SyncOutlined />;
            default: return <ExclamationCircleOutlined />;
        }
    };

    const getPlanColor = (type: string) => {
        switch (type?.toLowerCase()) {
            case 'monthly': return 'gold';
            case 'annually': return 'purple';
            case 'trial': return 'blue';
            default: return 'default';
        }
    };

    const columns: ColumnsType<CompanyPaymentRecord> = [
        {
            title: 'İşlem ID',
            dataIndex: 'id',
            key: 'id',
            width: 120,
            ellipsis: true,
            render: (id: string) => (
                <Text code className="small" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {id.slice(-8)}
                </Text>
            ),
        },
        {
            title: 'Şirket',
            dataIndex: 'companyName',
            key: 'companyName',
            width: 180,
            ellipsis: true,
            render: (name: string) => (
                <div>
                    <Text strong>{name}</Text>
                </div>
            ),
        },
        {
            title: 'Plan',
            dataIndex: 'planType',
            key: 'planType',
            width: 190,
            render: (planType: string, record: CompanyPaymentRecord) => (
                <Space size={6} wrap={false}>
                    <Tag color={getPlanColor(planType)}>{planType?.toUpperCase()}</Tag>
                    <Text type="secondary" className="small">{record.userLimit} kullanıcı</Text>
                </Space>
            ),
        },
        // Açıklama tablodan kaldırıldı (modalda gösterilecek)
        {
            title: 'Tutar',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right' as const,
            sorter: (a, b) => a.amount - b.amount,
            render: (amount: number, record: CompanyPaymentRecord) => (
                <div className="text-end">
                    {(() => {
                        const color = record.paymentStatus === 'pending'
                          ? '#faad14' // sarı
                          : record.paymentStatus === 'paid'
                            ? '#3f8600' // yeşil
                            : record.paymentStatus === 'failed'
                              ? '#ff4d4f' // kırmızı
                              : undefined;
                        return (
                          <Text strong style={{ color }}>
                            {formatCurrency(amount, record.currency)}
                          </Text>
                        );
                    })()}
                </div>
            ),
        },
        {
            title: 'Durum',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            width: 140,
            filters: [
                { text: 'Ödendi', value: 'paid' },
                { text: 'Bekliyor', value: 'pending' },
                { text: 'Başarısız', value: 'failed' },
                { text: 'İade Edildi', value: 'refunded' },
            ],
            onFilter: (value, record) => record.paymentStatus === value,
            render: (status: string) => (
                <Badge 
                    status={getStatusColor(status) as any}
                    text={
                        <span>
                            {getStatusIcon(status)} {getStatusText(status)}
                        </span>
                    }
                />
            ),
        },
        {
            title: 'Ödeme Tarihi',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            width: 140,
            sorter: (a, b) => dayjs(a.paymentDate).unix() - dayjs(b.paymentDate).unix(),
            render: (date: string) => (
                <div>
                    <div>{date ? dayjs(date).format('DD.MM.YYYY') : '-'}</div>
                    <Text type="secondary" className="small">
                        {date ? dayjs(date).format('HH:mm') : ''}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Süre',
            key: 'duration',
            width: 170,
            render: (record: CompanyPaymentRecord) => (
                <div>
                    <div className="small">
                        <CalendarOutlined className="me-1" />
                        {record.paymentDate ? dayjs(record.paymentDate).format('DD.MM.YYYY') : '-'}
                    </div>
                    <div className="small text-muted">
                        {record.expiryDate ? dayjs(record.expiryDate).format('DD.MM.YYYY') : '—'}
                    </div>
                </div>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 80,
            render: (record: CompanyPaymentRecord) => (
                <Space>
                    <Tooltip title="Detayları Görüntüle">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => {
                                setSelectedPayment((record as any).raw || record);
                                setDetailOpen(true);
                            }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const filteredPayments = payments.filter(payment => 
        payment.companyName?.toLowerCase().includes(searchText?.toLowerCase()) ||
        payment.id?.toLowerCase().includes(searchText?.toLowerCase()) ||
        payment.description?.toLowerCase().includes(searchText?.toLowerCase())
    );

    const handleExport = () => {
        // Excel/CSV export işlemi
        // console.log('Export payments data');
    };

    if (loading && payments.length === 0) {
        return (
            <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
                <div className="content flex-fill h-100">
                    <div className="p-6 text-center">
                        <Spin size="large" />
                        <div className="mt-3">
                            <Text>Ödeme verileri yükleniyor...</Text>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
            <div className="content flex-fill h-100">
                <div className="p-6">
                    <div className="mb-6">
                        <Title level={3}>Ödeme Raporları</Title>
                        <Text type="secondary">
                            Tüm şirketlerin ödeme geçmişi ve abonelik durumları
                        </Text>
                    </div>

                    {/* İstatistikler */}
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Toplam Gelir"
                                    value={stats?.totalRevenue}
                                    precision={2}
                                    prefix={<DollarOutlined />}
                                    suffix="₺"
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Aylık Gelir"
                                    value={stats?.monthlyRevenue}
                                    precision={2}
                                    prefix={<CalendarOutlined />}
                                    suffix="₺"
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Aktif Abonelik"
                                    value={stats?.activeSubscriptions}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Deneme Kullanıcıları"
                                    value={stats?.trialUsers}
                                    prefix={<TeamOutlined />}
                                    valueStyle={{ color: '#faad14' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="İptal Edilen"
                                    value={stats?.canceledSubscriptions}
                                    prefix={<CloseCircleOutlined />}
                                    valueStyle={{ color: '#ff4d4f' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Kullanıcı Başına Gelir"
                                    value={stats?.averageRevenuePerUser}
                                    precision={2}
                                    prefix={<TrophyOutlined />}
                                    suffix="₺"
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtreler */}
                    <Card className="mb-4">
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={12} md={6}>
                                <Input
                                    placeholder="Şirket adı ara..."
                                    prefix={<SearchOutlined />}
                                    value={filters.companyName}
                                    onChange={(e) => setFilters({ ...filters, companyName: e.target.value })}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Ödeme durumu"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filters.paymentStatus}
                                    onChange={(value) => setFilters({ ...filters, paymentStatus: value })}
                                >
                                    <Option value="paid">Ödendi</Option>
                                    <Option value="pending">Bekliyor</Option>
                                    <Option value="failed">Başarısız</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Plan tipi"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filters.planType}
                                    onChange={(value) => setFilters({ ...filters, planType: value })}
                                >
                                    <Option value="trial">Deneme</Option>
                                    <Option value="premium">Premium</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<ReloadOutlined />}
                                        onClick={fetchPaymentData}
                                        loading={loading}
                                    >
                                        Yenile
                                    </Button>
                                    {/* <Button
                                        icon={<DownloadOutlined />}
                                        onClick={handleExport}
                                    >
                                        Dışa Aktar
                                    </Button> */}
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    {/* Ödeme Tablosu */}
                    <Card>
                        <Table
                            columns={columns}
                            dataSource={filteredPayments}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 20,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} / ${total} ödeme`,
                            }}
                             scroll={{ x: 1100 }}
                            size="small"
                        />
                    </Card>
                </div>
            </div>
            <Modal
                title="Ödeme Detayı"
                open={detailOpen}
                onCancel={() => setDetailOpen(false)}
                footer={null}
                width={720}
            >
                {selectedPayment && (
                    <>
                    <Descriptions bordered size="small" column={2} labelStyle={{ width: 180 }}>
                        <Descriptions.Item label="İşlem ID">{String(selectedPayment.id).slice(-12)}</Descriptions.Item>
                        <Descriptions.Item label="Şirket">{selectedPayment.companyName}</Descriptions.Item>
                        <Descriptions.Item label="Plan">
                            <Space>
                                <Tag color={getPlanColor(selectedPayment.planType)}>{String(selectedPayment.planType).toUpperCase()}</Tag>
                                <span>{selectedPayment.purchasedLimit || selectedPayment.userLimit} kullanıcı</span>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Durum">
                            <Badge status={getStatusColor(selectedPayment.paymentStatus) as any} text={getStatusText(selectedPayment.paymentStatus)} />
                        </Descriptions.Item>
                        <Descriptions.Item label="Tutar">{formatCurrency(Number((selectedPayment.amountPaid ?? selectedPayment.amount ?? 0)), selectedPayment.currency || 'TRY')}</Descriptions.Item>
                        <Descriptions.Item label="Para Birimi">{selectedPayment.currency || 'TRY'}</Descriptions.Item>
                        <Descriptions.Item label="Ödeme Tarihi">{selectedPayment.paidAt ? dayjs(selectedPayment.paidAt).format('DD.MM.YYYY HH:mm') : '-'}</Descriptions.Item>
                        <Descriptions.Item label="Başlangıç">{selectedPayment.startDate ? dayjs(selectedPayment.startDate).format('DD.MM.YYYY HH:mm') : '-'}</Descriptions.Item>
                        <Descriptions.Item label="Bitiş">{selectedPayment.expiryDate ? dayjs(selectedPayment.expiryDate).format('DD.MM.YYYY HH:mm') : '—'}</Descriptions.Item>
                        <Descriptions.Item label="Süre (gün)">{selectedPayment.durationDays ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="Açıklama" span={2}>{selectedPayment.description || '-'}</Descriptions.Item>
                        {selectedPayment.couponCode || selectedPayment.couponDiscountAmount ? (
                          <Descriptions.Item label="Kupon" span={2}>
                            <Space size={12} wrap>
                              <span>Kod: <Text code>{selectedPayment.couponCode || '-'}</Text></span>
                              <span>İndirim: {selectedPayment.couponDiscountAmount ? formatCurrency(Number(selectedPayment.couponDiscountAmount), selectedPayment.currency || 'TRY') : '-'}</span>
                            </Space>
                          </Descriptions.Item>
                        ) : null}
                        {selectedPayment.card ? (
                          <Descriptions.Item label="Kart" span={2}>
                            <Space size={12} wrap>
                              <span>Tip: {selectedPayment.card?.type || '-'}</span>
                              <span>Marka: {selectedPayment.card?.association || '-'}</span>
                              <span>Aile: {selectedPayment.card?.family || '-'}</span>
                              <span>BIN: {selectedPayment.card?.bin || '-'}</span>
                            </Space>
                          </Descriptions.Item>
                        ) : null}
                        {selectedPayment.parasutInvoiceId || selectedPayment.parasutInvoiceStatus ? (
                          <Descriptions.Item label="Fatura (Paraşüt)" span={2}>
                            <Space size={12} wrap>
                              <span>ID: {selectedPayment.parasutInvoiceId || '-'}</span>
                              <span>Durum: {selectedPayment.parasutInvoiceStatus || '-'}</span>
                            </Space>
                          </Descriptions.Item>
                        ) : null}
                        {(selectedPayment.amountPaid != null || selectedPayment.netAmount != null || selectedPayment.iyzicoCommissionFee != null || selectedPayment.vatAmount != null || selectedPayment.vatRate != null || selectedPayment.netAfterTax != null) ? (
                          <Descriptions.Item label="Finans" span={2}>
                            {(() => {
                              const currency = selectedPayment.currency || 'TRY';
                              const gross = selectedPayment.amountPaid != null ? Number(selectedPayment.amountPaid) : null;
                              const net = selectedPayment.netAmount != null ? Number(selectedPayment.netAmount) : null;
                              const rawCommission = selectedPayment.iyzicoCommissionFee != null
                                ? Number(selectedPayment.iyzicoCommissionFee)
                                : (gross != null && net != null ? Math.max(0, gross - net) : null);
                              const vatRate = selectedPayment.vatRate != null ? `${Number(selectedPayment.vatRate)}%` : '-';
                              const vatAmount = selectedPayment.vatAmount != null ? formatCurrency(Number(selectedPayment.vatAmount), currency) : '-';
                              const netAfterTax = selectedPayment.netAfterTax != null ? formatCurrency(Number(selectedPayment.netAfterTax), currency) : '-';
                              return (
                                <Space size={16} wrap>
                                  <span>
                                    <Text type="secondary">Brüt:</Text> <Text strong>{gross != null ? formatCurrency(gross, currency) : '-'}</Text>
                                  </span>
                                  <span>
                                    <Text type="secondary">İyzico:</Text> <Text strong style={{ color: '#fa541c' }}>{rawCommission != null ? formatCurrency(rawCommission, currency) : '-'}</Text>
                                  </span>
                                  <span>
                                    <Text type="secondary">KDV Oranı:</Text> <Text strong>{vatRate}</Text>
                                  </span>
                                  <span>
                                    <Text type="secondary">KDV:</Text> <Text strong>{vatAmount}</Text>
                                  </span>
                                  <span>
                                    <Text type="secondary">Net (Komisyon):</Text> <Text strong>{net != null ? formatCurrency(net, currency) : '-'}</Text>
                                  </span>
                                  <span>
                                    <Text type="secondary">Net (Vergi):</Text> <Text strong>{netAfterTax}</Text>
                                  </span>
                                </Space>
                              );
                            })()}
                          </Descriptions.Item>
                        ) : null}
                    </Descriptions>
                
                    </>
                )}
            </Modal>
        </div>
    );
};

export default PaymentReportsPage; 